import { ActivationOptionsResponse } from '@bingads-webui-campaign-react/accept-invitation-api';
import type { DebugErrorPage } from '../query-params';

// Maps a debug scenario (?acceptinvitationdebug=…) to a mocked ActivationOptions response so the
// debug param drives the EXACT same render path as a real API response — no separate debug branch.
// `fallbackerror` returns an unhandled type so it lands on the general <ActivationErrorFallback>.
export const getDebugActivationOptions = (debug: DebugErrorPage | null): ActivationOptionsResponse | undefined => {
  switch (debug) {
    case 'aadrequired':
      return {
        type: 'ActivateWithAlternate', expectedIdentityType: 'AAD', currentIdentityType: 'MSA', expectedUsername: 'abc@contoso.com',
      };
    case 'msarequired':
      return {
        type: 'ActivateWithAlternate', expectedIdentityType: 'MSA', currentIdentityType: 'AAD', expectedUsername: 'abc@outlook.com',
      };
    case 'googlerequired':
      return {
        type: 'ActivateWithAlternate', expectedIdentityType: 'Google', currentIdentityType: 'MSA', expectedUsername: 'abc@gmail.com',
      };
    case 'facebookrequired':
      return {
        type: 'ActivateWithAlternate', expectedIdentityType: 'Facebook', currentIdentityType: 'MSA', expectedUsername: 'abc@facebook.com',
      };
    case 'denied':
      return {
        type: 'ActivationDenied', reason: 'PuidMatch_MatchesUserCannotSignIn', username: 'abc@example.com', identityType: 'MSA',
      };
    case 'alternatemerge':
      // Same-provider ActivateWithAlternate, ownership proven (PuidMatch non-login "user name
      // deactivated") — offers Continue as the matched account + use a different email.
      return {
        type: 'ActivateWithAlternate',
        reason: 'PuidMatch_MatchesNonLoginUserWhoseLoginUserCanBeMerged',
        expectedUsername: 'jordan@outlook.com',
        expectedIdentityType: 'MSA',
        currentUsername: 'jo.lee@hotmail.com',
        currentIdentityType: 'MSA',
      };
    case 'alternateblocked':
      // Same-provider ActivateWithAlternate, ownership NOT proven (AliasMatch) — a dead-end that only
      // offers "use a different email" and does NOT reveal the matched account.
      return {
        type: 'ActivateWithAlternate',
        reason: 'AliasMatch_MatchesUserCanBeMerged',
        expectedUsername: 'jordan@outlook.com',
        expectedIdentityType: 'MSA',
        currentUsername: 'jo.lee@hotmail.com',
        currentIdentityType: 'MSA',
      };
    case 'alreadyhasaccess':
      // ActivationDenied with the "already a user under the same customer" reason — the invited email
      // already has access, so it drives the reassuring <ActivationAlreadyHasAccess> page rather than
      // the generic "this account can't be used" denied dialog.
      return {
        type: 'ActivationDenied', reason: 'PuidMatch_MatchesUserUnderSameCustomer', username: 'abc@example.com', identityType: 'MSA',
      };
    case 'existingprofile':
      // A consent-only MultiuserActivation (complete existing address) — combined with create-intent
      // (see isCreateIntent below) this drives the "An existing profile was found" dialog.
      return {
        type: 'MultiuserActivation',
        username: 'abc@example.com',
        identityType: 'MSA',
        isMissingMasterData: false,
        existingUser: {
          firstName: 'Jordan',
          lastName: 'Lee',
          email: 'abc@example.com',
          address: {
            addressLine1: '123 Main St', city: 'Seattle', stateOrProvince: 'WA', zipOrPostalCode: '98101', country: 'US',
          },
        },
      };
    case 'partialaddress':
      // A MultiuserActivation whose address is missing `city` — one of the three fields
      // (addressLine1 + city + country) that gate consent-only — so it renders the profile form with
      // the rest of the address prefilled, which is otherwise unreachable without server-side data.
      return {
        type: 'MultiuserActivation',
        username: 'abc@example.com',
        identityType: 'MSA',
        isMissingMasterData: false,
        existingUser: {
          firstName: 'Jordan',
          lastName: 'Lee',
          email: 'abc@example.com',
          address: {
            addressLine1: '123 Main St', addressLine2: 'Suite 400', stateOrProvince: 'WA', zipOrPostalCode: '98101', country: 'US',
          },
        },
      };
    case 'fallbackerror':
      return { type: 'Unknown' };
    default:
      return undefined;
  }
};
